import { Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface InsightsPanelProps {
  open: boolean;
  address: string;
  addressId: string;
  propertyData: any;
  cadastreData: any;
  buildingData: any;
}

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-1">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    <div className="text-sm">{children}</div>
  </div>
);

const Stars = ({ value, max = 5 }: { value: number; max?: number }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: max }).map((_, i) => (
      <Star
        key={i}
        className={cn(
          "size-4",
          i < value
            ? "fill-yellow-400 text-yellow-400"
            : "fill-muted text-muted-foreground",
        )}
      />
    ))}
  </div>
);

const RiskGauge = ({
  value,
  label,
  text,
}: {
  value: number;
  label: string;
  text: string;
}) => {
  const size = 100;
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            className="fill-none stroke-muted"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            className="fill-none stroke-primary transition-all"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-sm font-medium">
          {text}
        </div>
      </div>
      <p className="text-sm">{label}</p>
    </div>
  );
};

const formatArea = (n: number): string =>
  n.toLocaleString("en-AU", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });

export const InsightsPanel = (props: InsightsPanelProps) => {
  const totalBuildingArea = props.buildingData.data.reduce(
    (n: number, { area }: { area: number }) => n + area,
    0,
  );
  const hasPool = props.buildingData.data.some(
    (data: { swimmingPool?: boolean }) => data.swimmingPool === true,
  );
  const hasSolar = props.buildingData.data.some(
    (data: { solarPanel?: boolean }) => data.solarPanel === true,
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <Card>
        <CardHeader>
          <CardTitle>{props.address}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-row gap-4">
            <Field label="Status">Policy Valid</Field>
            <Field label="Insight Capture Date">11th Jan 2024</Field>
            <Field label="Policy Review Date">6th Mar 2024</Field>
            <Field label="Policy Health">
              <Stars value={4} />
            </Field>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">Notes</Label>
            <Textarea rows={4} />
          </div>
        </CardContent>
        <CardFooter className="gap-2">
          <Button variant="ghost" size="sm">
            Share
          </Button>
          <Button variant="ghost" size="sm">
            Export
          </Button>
          <Button variant="ghost" size="sm">
            Request Refresh
          </Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Property</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          <div className="flex flex-row justify-between gap-2">
            <Field label="Property Identifier">
              <Badge variant="secondary">
                {props.propertyData.features[0].properties.propertyPid}
              </Badge>
            </Field>
            <Field label="Strata Properties">None</Field>
            <Field label="Parcels">
              <div className="flex w-37.5 flex-row gap-1 overflow-scroll">
                {props.propertyData.features[0].properties.parcelIds.map(
                  (parcelId: string) => (
                    <Badge key={parcelId} variant="secondary">
                      {parcelId}
                    </Badge>
                  ),
                )}
              </div>
            </Field>
            <Field label="Planning Zone">
              {
                props.cadastreData.features[0].properties?.planningBundle
                  ?.psmaDescription
              }
            </Field>
          </div>
          <div className="flex flex-row justify-between gap-2">
            <Field label="Total Area">
              {formatArea(props.cadastreData.totalArea)} sq m
            </Field>
            <Field label="Total Building Area">
              {formatArea(totalBuildingArea)} sq m
            </Field>
            <Field label="Total Green space">111 sq m</Field>
            <Field label="Concrete/tar">123 sq m</Field>
          </div>
          <div className="flex flex-row justify-between gap-2">
            <Field label="Property Slope">0-5 deg</Field>
            <Field label="Driveway">2</Field>
            <Field label="Street Frontage">36 m</Field>
            <Field label="Pool">{hasPool ? "Detected" : "None"}</Field>
            <Field label="Solar">{hasSolar ? "Detected" : "None"}</Field>
            <Field label="Car parks">35</Field>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Buildings</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          <div className="flex flex-row gap-2">
            <Badge>1-Primary</Badge>
            <Badge variant="secondary">2-Secondary</Badge>
            <Badge variant="secondary">3-Secondary</Badge>
            <Badge variant="secondary">4-Primary</Badge>
            <Badge variant="secondary">Combined Stats</Badge>
          </div>
          <div className="flex flex-row gap-4">
            <Field label="Total Area">
              {formatArea(props.cadastreData.totalArea)} sq m
            </Field>
            <Field label="Total Building Area">
              {formatArea(totalBuildingArea)} sq m
            </Field>
            <Field label="Total Green Space">232 sq m</Field>
            <Field label="concrete/tar">505 sq m</Field>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Risk</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-row justify-between">
          <RiskGauge value={20} label="Climate" text="Low" />
          <RiskGauge value={20} label="Crime" text="Low" />
          <RiskGauge value={20} label="Fire" text="Low" />
          <RiskGauge value={20} label="Flood" text="Low" />
        </CardContent>
      </Card>
    </div>
  );
};
